import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  Tab,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import { AdminUser, hasRole } from "../../models/User";
import MaterialTable from "@material-table/core";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import useStyles from "./styles";
import { UserContext } from "../../UserProvider";
import { listWithTenantAccess } from "../../api/personApi";
import { isOk } from "../../result";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store";
import { Tenant } from "../../models/Tenant";
import { thunkFetchTenants } from "../../store/tenants/actions";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import DialogActions from "@material-ui/core/DialogActions";
import Alert from "@material-ui/lab/Alert";
import { Person } from "../../models/Person";
import * as managerApi from "../../api/personApi";
import { Settings } from "@material-ui/icons";
import { Badge } from "@mui/icons-material";
import { TenantMultiSelect } from "../../components/selects/TenantMultiSelect";
import { Store } from "../../models/Store";
import { TabContext, TabList, TabPanel } from "@material-ui/lab";
import { StoreSettings } from "../toolbar/usersettings/components/StoreSettings";
import { Profile } from "../toolbar/usersettings/components/Profile";
import AdministratorEventsCollection from "./AdministratorEventsCollection";
import { useI18n } from "../../hooks/useI18n";

export const PersonnelList = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useContext(UserContext);
  const { t } = useI18n();

  const tenants =
    useSelector<AppState, ReadonlyArray<Tenant>>(s => s.tenants.list) || [];

  const [selectedTab, setSelectedTab] = useState("1");
  const [showPersonId, setShowPersonId] = useState(params.personId || "");
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [state, setState] = useState({
    isLoading: false,
    reload: false,
    managers: [] as any,
    selectedManager: {} as any
  });

  useEffect(() => {
    setState({ ...state, isLoading: true });
    if ((tenants && tenants.length) || state.reload) {
      listWithTenantAccess().then(result => {
        if (isOk(result)) {
          let selectedManager = {} as any;
          if (showPersonId !== "") {
            selectedManager = result.value.find(x => x.id === showPersonId);
          }
          setState({
            ...state,
            managers: result.value,
            selectedManager: selectedManager,
            isLoading: false,
            reload: false
          });
        }
      });
    }
  }, [tenants, state.reload]);

  const getTenantName = (id: string) => {
    const tenant = tenants.find(x => x.id === id);
    return tenant != null ? tenant.name : "";
  };

  const getTenantAccess = (ids: string[]) => {
    const tenantList = tenants.filter((x: any) => ids.find(y => y === x.id));
    return tenantList.length > 0
      ? tenantList.flatMap(x => x.name).join(", ")
      : "";
  };

  const validatePhoneNumber = (phone: string) => {
    if (!phone) return false;

    const regex = /^[+]{1}[0-9]{9,}$/;

    return !phone.match(regex);
  };

  const confirm = async () => {
    setShowError(false);
    setState({ ...state, isLoading: true });

    const result = await managerApi.updateManager(state.selectedManager.id, {
      ...state.selectedManager,
      tenantIds: state.selectedManager.tenantAccessList
    } as AdminUser);
    if (isOk(result)) {
      setState({ ...state, reload: true });
      setShowPersonId("");
      setShowSuccess(true);
      navigate("/personnel", { replace: true });
    } else {
      setShowError(true);
    }
    setState({ ...state, isLoading: false });
  };

  return hasRole(user, ["Root", "Support"]) ? (
    <>
      <MaterialTable
        title={t("common.messages.personnel")}
        isLoading={state.isLoading}
        columns={[
          {
            title: "Id",
            field: "id",
            hidden: true,
            searchable: true
          },
          {
            title: "Name",
            field: "name",
            export: false
          },
          {
            title: "Username",
            field: "email",
            searchable: true
          },
          {
            title: "Email",
            field: "contactEmail",
            searchable: true
          },
          {
            title: "Phonenumber",
            field: "phoneNumber",
            searchable: true,
            export: true
          },
          {
            title: t("common.formFields.activeTenant"),
            field: "tenantId",
            searchable: true,
            export: true,
            render: (row: any) => getTenantName(row.tenantId)
          },
          {
            title: t("common.formFields.tenantAccess"),
            field: "tenantAccessList",
            searchable: true,
            export: true,
            render: (row: any) => getTenantAccess(row.tenantAccessList)
          }
        ]}
        data={state.managers as []}
        onRowClick={(evt, row) => {
          setState({ ...state, selectedManager: row });
          setShowPersonId(row?.id);
          if (row?.id) {
            navigate(row?.id);
          }
        }}
        options={{
          pageSize: 20,
          exportMenu: !mobile
            ? [
                {
                  label: t("common.export.exportPdf"),
                  exportFunc: (cols, data) => ExportPdf(cols, data, `Users`)
                },
                {
                  label: t("common.export.exportCsv"),
                  exportFunc: (cols, data) => ExportCsv(cols, data, `Users`)
                }
              ]
            : [],
          exportAllData: true
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
      />

      <Dialog
        onClose={() => {
          navigate("/personnel", { replace: true });
          setShowPersonId("");
          setSelectedTab("1");
        }}
        open={showPersonId !== ""}
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"md"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle
          disableTypography
          style={{ display: "flex", alignItems: "center", paddingBottom: 15 }}
        >
          <Avatar
            style={{
              backgroundColor: theme.palette.primary.main,
              display: "inline-flex",
              marginRight: 10
            }}
          >
            <Badge />
          </Avatar>
          <Typography variant={"h6"} display={"inline"}>
            {state.selectedManager?.name}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              navigate("/personnel", { replace: true });
              setShowPersonId("");
              setSelectedTab("1");
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        {state.isLoading && (
          <div className={classes.spinnerContainer}>
            <CircularProgress />
          </div>
        )}
        <TabContext value={selectedTab}>
          <TabList
            scrollButtons="auto"
            variant="scrollable"
            style={{ borderBottom: "1px solid #e0e0e0" }}
            onChange={(event: React.ChangeEvent<{}>, newValue: string) => {
              setSelectedTab(newValue);
            }}
          >
            <Tab label={"Profile"} value="1" />
            <Tab label={"Events"} value="2" />
          </TabList>
          <TabPanel value={"1"} title={"Profile"}>
            <DialogContent>
              <Container>
                <Grid item container spacing={2} className={classes.center}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      style={{ flex: 1 }}
                      className={classes.field}
                      label={t("common.formFields.name")}
                      value={state?.selectedManager.name ?? ""}
                      onChange={(e: any) => {
                        setState({
                          ...state,
                          selectedManager: {
                            ...state.selectedManager,
                            name: e.target.value
                          }
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      style={{ flex: 1 }}
                      className={classes.field}
                      label={t("common.formFields.email")}
                      value={state?.selectedManager.contactEmail ?? ""}
                      onChange={(e: any) => {
                        setState({
                          ...state,
                          selectedManager: {
                            ...state.selectedManager,
                            contactEmail: e.target.value
                          }
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      helperText={t("common.formFields.phoneNumberHelperText")}
                      error={validatePhoneNumber(
                        state?.selectedManager.phoneNumber
                      )}
                      style={{ flex: 1 }}
                      className={classes.field}
                      label={t("common.formFields.phoneNumber")}
                      value={state?.selectedManager.phoneNumber ?? ""}
                      onChange={(e: any) => {
                        setState({
                          ...state,
                          selectedManager: {
                            ...state.selectedManager,
                            phoneNumber: e.target.value
                          }
                        });
                      }}
                    />
                  </Grid>
                  <Box sx={{ width: "100%" }} />
                  <Grid item xs={12}>
                    <TenantMultiSelect
                      label={t("common.formFields.tenantAccess")}
                      className={classes.field}
                      value={state?.selectedManager?.tenantAccessList?.map(
                        (x: any) => x
                      )}
                      onSelected={s =>
                        setState({
                          ...state,
                          selectedManager: {
                            ...state.selectedManager,
                            tenantAccessList: s.map(x => x.id)
                          }
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Container>
              <DialogActions style={{ padding: 0 }}>
                <Container>
                  <Box
                    pr={0}
                    pb={1}
                    mt={2}
                    display={"flex"}
                    justifyContent={"end"}
                  >
                    <Button
                      onClick={confirm}
                      color="primary"
                      autoFocus
                      variant={"contained"}
                      disabled={state.isLoading}
                    >
                      Save
                    </Button>
                  </Box>
                </Container>
              </DialogActions>
              {showError && (
                <Alert severity="error">
                  {t("common.messages.failedToSaveProfile")}
                </Alert>
              )}
            </DialogContent>
          </TabPanel>
          <TabPanel value={"2"} title={"Events"}>
            <AdministratorEventsCollection userId={state.selectedManager?.id} />
          </TabPanel>
        </TabContext>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => {
          setShowSuccess(false);
        }}
      >
        <Alert severity={"success"}>
          {t("common.messages.profileUpdatedSuccessfully")}
        </Alert>
      </Snackbar>
    </>
  ) : (
    <></>
  );
};
